import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import React, { useMemo, useCallback } from 'react';
import { ATTRIBUTE_ORDER, STUDY_KEY_TITLE } from '../constant';
import RadioGroup from '../RadioGroup';
import { Study, StudyAttribute, Indication } from '../types';
import type { StudyKeyConfig } from '../types';
import IndicationComparisonActions from './IndicationComparisonActions';
import IndicationComparisonTitle from './IndicationComparisonTitle';
import IndicationSummaryTable from './IndicationSummaryTable';
import StudyComparisonBlock from './StudyComparisonBlock';
import styles from './styles';

const EMPTY = '—';
const SAMPLE_SIZE_ATTRIBUTE_KEY = 'samplesize';

type StudyComparisonMatchType = 'identifier' | 'sample-size' | 'unmatched';

interface StudyComparisonSlot {
  slotKey: string;
  prevStudy: Study | null;
  currStudy: Study | null;
  matchType: StudyComparisonMatchType;
}

type ComparisonAttributeRow = Pick<StudyAttribute, 'attribute_name' | 'attribute_id'>;

function formatStudyValue(key: string, value: unknown): string {
  if (value == null || value === '') return EMPTY;
  const config = STUDY_KEY_TITLE[key];
  if (config?.options && typeof value === 'boolean') {
    const opt = config.options.find((o: string | { label: string; value: boolean }) =>
      typeof o === 'object' ? o.value === value : false
    );
    return (typeof opt === 'object' ? opt?.label : undefined) ?? String(value);
  }
  return String(value);
}

/* eslint-disable-next-line react/require-default-props */
interface EditableStudyKeyCellProps {
  fieldKey: string;
  value: unknown;
  config: StudyKeyConfig;
  studyId: string | number;
  // eslint-disable-next-line react/require-default-props, no-unused-vars
  onUpdateStudyField?: (key: string, value: string | number | boolean) => void;
}

const EditableStudyKeyCell = ({
  fieldKey,
  value,
  config,
  studyId,
  onUpdateStudyField
}: EditableStudyKeyCellProps): React.ReactElement => {
  const handleRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyField?.(fieldKey, e.target.value === 'true');
    },
    [fieldKey, onUpdateStudyField]
  );

  const handleAutocompleteChange = useCallback(
    (_e: React.SyntheticEvent, newValue: string) => {
      onUpdateStudyField?.(fieldKey, newValue ?? '');
    },
    [fieldKey, onUpdateStudyField]
  );

  const renderAutocompleteInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        id={params.id}
        fullWidth={params.fullWidth}
        size={params.size}
        type={config.inputType}
        placeholder={`Enter ${config.title}`}
        InputProps={{
          ...params.InputProps,
          inputProps: params.inputProps
        }}
        InputLabelProps={params.InputLabelProps}
        disabled={params.disabled}
      />
    ),
    [config.inputType, config.title]
  );

  const handleFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyField?.(fieldKey, e.target.value);
    },
    [fieldKey, onUpdateStudyField]
  );

  if (config.inputType === 'radio-group' && config.options) {
    const options = config.options
      .filter((o): o is { label: string; value: boolean } => typeof o === 'object')
      .map(o => ({ label: o.label, value: String(o.value) }));
    return (
      <RadioGroup
        id={`edit-${studyId}-${fieldKey}`}
        value={value === true || value === false ? String(value) : ''}
        options={options}
        onChange={handleRadioChange}
      />
    );
  }

  if (config.inputType === 'text' || config.inputType === 'number') {
    const options = config.options || [];
    if (options.length > 0) {
      return (
        <Autocomplete
          freeSolo
          disableClearable
          size='small'
          fullWidth
          options={options}
          value={value ?? ''}
          onInputChange={handleAutocompleteChange}
          renderInput={renderAutocompleteInput}
        />
      );
    }
    return (
      <TextField
        size='small'
        fullWidth
        type={config.inputType}
        value={value ?? ''}
        onChange={handleFieldChange}
        placeholder={`Enter ${config.title}`}
      />
    );
  }

  return <>{formatStudyValue(fieldKey, value)}</>;
};

interface EditableAttributeCellProps {
  attrName: string;
  attrId: number;
  value: string;
  // eslint-disable-next-line react/require-default-props, no-unused-vars
  onUpdateStudyAttribute?: (attributeName: string, attributeId: number, value: string) => void;
}

const EditableAttributeCell = ({
  attrName,
  attrId,
  value,
  onUpdateStudyAttribute
}: EditableAttributeCellProps) => {
  const handleAttributeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyAttribute?.(attrName, attrId, e.target.value);
    },
    [attrName, attrId, onUpdateStudyAttribute]
  );
  return <TextField size='small' fullWidth value={value} onChange={handleAttributeChange} />;
};

function getAttributeValue(
  study: Study | null,
  attributeName: string,
  attributeId?: number
): string {
  if (!study?.attributes) return EMPTY;
  const attr = study.attributes.find(
    (a: StudyAttribute) =>
      a.attribute_name === attributeName ||
      (attributeId != null && Number(a.attribute_id) === Number(attributeId))
  );
  const val = attr?.attribute_value;
  return val != null && val !== '' ? String(val) : EMPTY;
}

const STUDY_KEYS_ORDER = ATTRIBUTE_ORDER.filter((k: string) => STUDY_KEY_TITLE[k]);

function getStudyKey(
  study: Study | null,
  identifierAttributeId: number | null | undefined,
  nctIdAttributeId: number | null | undefined
): string | number | null {
  if (!study) return null;
  const attrs = study.attributes ?? [];
  if (identifierAttributeId != null) {
    const identifierAttr = attrs.find(
      (a: StudyAttribute) => Number(a.attribute_id) === identifierAttributeId
    );
    const identifierVal = identifierAttr?.attribute_value;
    if (identifierVal != null && identifierVal !== '') {
      return String(identifierVal);
    }
  }
  if (nctIdAttributeId != null) {
    const nctAttr = attrs.find((a: StudyAttribute) => Number(a.attribute_id) === nctIdAttributeId);
    const nctVal = nctAttr?.attribute_value;
    if (nctVal != null && nctVal !== '') {
      return String(nctVal);
    }
  }
  return study.id ?? null;
}

function normalizeAttributeName(attributeName: string): string {
  return attributeName.replace(/\s/g, '').toLowerCase();
}

function normalizeSampleSizeValue(value: unknown): string | null {
  if (value == null || value === '') return null;
  const rawValue = String(value).trim();
  if (!rawValue) return null;
  const numericValue = rawValue.replace(/,/g, '');
  if (/^\d+(\.\d+)?$/.test(numericValue)) {
    return String(Number(numericValue));
  }
  return rawValue.toLowerCase();
}

function getSampleSizeKey(study: Study | null): string | null {
  const sampleSizeAttr = study?.attributes?.find(
    (a: StudyAttribute) => normalizeAttributeName(a.attribute_name) === SAMPLE_SIZE_ATTRIBUTE_KEY
  );
  return normalizeSampleSizeValue(sampleSizeAttr?.attribute_value);
}

function getStudySortKey(
  study: Study,
  identifierAttributeId: number | null | undefined,
  nctIdAttributeId: number | null | undefined
): string {
  const studyKey = getStudyKey(study, identifierAttributeId, nctIdAttributeId);
  return String(studyKey ?? getSampleSizeKey(study) ?? study.id);
}

function makeSlotKey(
  prefix: string,
  prevStudy: Study | null,
  currStudy: Study | null,
  fallbackKey: string
): string {
  return [prefix, fallbackKey, prevStudy?.id ?? 'none', currStudy?.id ?? 'none'].join(':');
}

function getAttributesForStudy(
  prevStudy: Study | null,
  currStudy: Study | null
): ComparisonAttributeRow[] {
  const attributeByName = new Map<string, number>();
  const addAttribute = (attr: StudyAttribute) => {
    if (!attr.attribute_name || attr.attribute_id == null) return;
    if (!attributeByName.has(attr.attribute_name)) {
      attributeByName.set(attr.attribute_name, Number(attr.attribute_id));
    }
  };
  currStudy?.attributes?.forEach(addAttribute);
  prevStudy?.attributes?.forEach(addAttribute);
  return Array.from(attributeByName.entries())
    .map(([attributeName, attributeId]) => ({
      attribute_name: attributeName,
      attribute_id: attributeId
    }))
    .sort((a, b) => a.attribute_name.localeCompare(b.attribute_name));
}

function getStudySlotsForComparison(
  prevStudies: Study[],
  currStudies: Study[],
  identifierAttributeId: number | null | undefined,
  nctIdAttributeId: number | null | undefined
): StudyComparisonSlot[] {
  const prevList = prevStudies ?? [];
  const currList = currStudies ?? [];
  const slots: StudyComparisonSlot[] = [];
  const matchedPrevIndexes = new Set<number>();
  const matchedCurrIndexes = new Set<number>();
  const currIndexesByStudyKey = new Map<string, number[]>();

  currList.forEach((study: Study, index: number) => {
    const studyKey = getStudyKey(study, identifierAttributeId, nctIdAttributeId);
    if (studyKey == null) return;
    const key = String(studyKey);
    const existing = currIndexesByStudyKey.get(key) ?? [];
    currIndexesByStudyKey.set(key, [...existing, index]);
  });

  prevList
    .map((study: Study, index: number) => ({
      study,
      index,
      studyKey: getStudyKey(study, identifierAttributeId, nctIdAttributeId)
    }))
    .filter(({ studyKey }) => studyKey != null)
    .sort((a, b) =>
      String(a.studyKey).localeCompare(String(b.studyKey), undefined, { numeric: true })
    )
    .forEach(({ study, index, studyKey }) => {
      const key = String(studyKey);
      const currIndexes = currIndexesByStudyKey.get(key) ?? [];
      const currIndex = currIndexes.find(candidateIndex => !matchedCurrIndexes.has(candidateIndex));
      if (currIndex == null) return;
      const currStudy = currList[currIndex];
      matchedPrevIndexes.add(index);
      matchedCurrIndexes.add(currIndex);
      slots.push({
        slotKey: makeSlotKey('identifier', study, currStudy, key),
        prevStudy: study,
        currStudy,
        matchType: 'identifier'
      });
    });

  prevList.forEach((study: Study, index: number) => {
    if (matchedPrevIndexes.has(index)) return;
    const sampleSizeKey = getSampleSizeKey(study);
    if (sampleSizeKey == null) return;
    const currIndex = currList.findIndex(
      (currStudy: Study, candidateIndex: number) =>
        !matchedCurrIndexes.has(candidateIndex) && getSampleSizeKey(currStudy) === sampleSizeKey
    );
    if (currIndex < 0) return;
    const currStudy = currList[currIndex];
    matchedPrevIndexes.add(index);
    matchedCurrIndexes.add(currIndex);
    slots.push({
      slotKey: makeSlotKey('sample-size', study, currStudy, sampleSizeKey),
      prevStudy: study,
      currStudy,
      matchType: 'sample-size'
    });
  });

  const previousOnly = prevList
    .map((study: Study, index: number) => ({ study, index }))
    .filter(({ index }) => !matchedPrevIndexes.has(index))
    .sort((a, b) =>
      getStudySortKey(a.study, identifierAttributeId, nctIdAttributeId).localeCompare(
        getStudySortKey(b.study, identifierAttributeId, nctIdAttributeId),
        undefined,
        { numeric: true }
      )
    );

  const currentOnly = currList
    .map((study: Study, index: number) => ({ study, index }))
    .filter(({ index }) => !matchedCurrIndexes.has(index))
    .sort((a, b) =>
      getStudySortKey(a.study, identifierAttributeId, nctIdAttributeId).localeCompare(
        getStudySortKey(b.study, identifierAttributeId, nctIdAttributeId),
        undefined,
        { numeric: true }
      )
    );

  previousOnly.forEach(({ study }) =>
    slots.push({
      slotKey: makeSlotKey(
        'previous-only',
        study,
        null,
        getStudySortKey(study, identifierAttributeId, nctIdAttributeId)
      ),
      prevStudy: study,
      currStudy: null,
      matchType: 'unmatched'
    })
  );

  currentOnly.forEach(({ study }) =>
    slots.push({
      slotKey: makeSlotKey(
        'current-only',
        null,
        study,
        getStudySortKey(study, identifierAttributeId, nctIdAttributeId)
      ),
      prevStudy: null,
      currStudy: study,
      matchType: 'unmatched'
    })
  );

  return slots;
}

/* eslint-disable react/require-default-props */
interface IndicationComparisonProps {
  title: string;
  onCopy?: () => void;
  onAcceptCurrent?: () => void;
  onCopyIndication?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCopyStudy?: (prevStudy: Study) => void;
  parentIndication: Indication | null;
  currentIndication: Indication | null;
  nctIdAttributeId: number | null;
  identifierAttributeId: number | null;
  showActions?: boolean;
  isCopying?: boolean;
  copyingStudyId?: string | number | null;
  editingStudyId?: number | string | null;
  editingStudyData?: Study | null;
  // eslint-disable-next-line no-unused-vars
  onEditStudy?: (study: Study) => void;
  onSaveStudy?: () => void;
  // eslint-disable-next-line no-unused-vars
  onUpdateStudyField?: (key: string, value: string | number | boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onUpdateStudyAttribute?: (attributeName: string, attributeId: number, value: string) => void;
}
/* eslint-enable react/require-default-props */

const IndicationComparison = ({
  title,
  parentIndication,
  currentIndication,
  nctIdAttributeId,
  identifierAttributeId,
  onCopy,
  onAcceptCurrent,
  onCopyIndication,
  onCopyStudy,
  showActions = true,
  isCopying = false,
  copyingStudyId = null,
  editingStudyId = null,
  editingStudyData = null,
  onEditStudy,
  onSaveStudy,
  onUpdateStudyField,
  onUpdateStudyAttribute
}: IndicationComparisonProps) => {
  const prevIndication = parentIndication ?? null;
  const currIndication = currentIndication ?? null;

  const prevStudies = prevIndication?.studies ?? [];
  const currStudies = currIndication?.studies ?? [];

  const studySlots = useMemo(
    () =>
      getStudySlotsForComparison(prevStudies, currStudies, identifierAttributeId, nctIdAttributeId),
    [prevStudies, currStudies, identifierAttributeId, nctIdAttributeId]
  );

  const prevStudyBySlotKey = useMemo(
    () =>
      new Map<string, Study>(
        studySlots
          .filter(
            (slot): slot is StudyComparisonSlot & { prevStudy: Study } => slot.prevStudy != null
          )
          .map(slot => [slot.slotKey, slot.prevStudy] as [string, Study])
      ),
    [studySlots]
  );

  const currStudyBySlotKey = useMemo(
    () =>
      new Map<string, Study>(
        studySlots
          .filter(
            (slot): slot is StudyComparisonSlot & { currStudy: Study } => slot.currStudy != null
          )
          .map(slot => [slot.slotKey, slot.currStudy] as [string, Study])
      ),
    [studySlots]
  );

  const handleCopyStudyClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const raw = (e.currentTarget as HTMLButtonElement).dataset.slotKey;
      if (raw == null) return;
      const prevStudy = prevStudyBySlotKey.get(raw);
      if (prevStudy) onCopyStudy?.(prevStudy);
    },
    [prevStudyBySlotKey, onCopyStudy]
  );

  const handleEditStudyClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const raw = (e.currentTarget as HTMLButtonElement).dataset.slotKey;
      if (raw == null) return;
      const currStudy = currStudyBySlotKey.get(raw);
      if (currStudy) onEditStudy?.(currStudy);
    },
    [currStudyBySlotKey, onEditStudy]
  );

  const renderCurrentCellForStudyKey = (
    key: string,
    currStudy: Study | null,
    studyId: string | number
  ) => {
    const isEditing =
      editingStudyId != null && currStudy?.id === editingStudyId && editingStudyData != null;
    const displayStudy = isEditing ? editingStudyData : currStudy;
    const value = displayStudy?.[key];

    if (!isEditing) {
      return formatStudyValue(key, value);
    }

    const config = STUDY_KEY_TITLE[key];
    if (!config) return formatStudyValue(key, value);

    return (
      <EditableStudyKeyCell
        fieldKey={key}
        value={value}
        config={config}
        studyId={studyId}
        onUpdateStudyField={onUpdateStudyField}
      />
    );
  };

  const renderCurrentCellForAttribute = (
    attrName: string,
    attrId: number,
    currStudy: Study | null
  ) => {
    const isEditing =
      editingStudyId != null && currStudy?.id === editingStudyId && editingStudyData != null;
    if (!isEditing) {
      return getAttributeValue(currStudy, attrName, attrId);
    }
    const attr = editingStudyData?.attributes?.find(
      (a: StudyAttribute) =>
        a.attribute_name === attrName || Number(a.attribute_id) === Number(attrId)
    );
    const valueStr = String(attr?.attribute_value ?? '');

    return (
      <EditableAttributeCell
        attrName={attrName}
        attrId={attrId}
        value={valueStr}
        onUpdateStudyAttribute={onUpdateStudyAttribute}
      />
    );
  };

  const hasAnyAction = Boolean(onCopy || onAcceptCurrent || (!currIndication && onCopyIndication));

  return (
    <Box height='100%' display='flex' flexDirection='column'>
      <IndicationComparisonTitle title={title} />

      <Box sx={styles.content}>
        <IndicationSummaryTable
          prevText={prevIndication?.text ?? null}
          currText={currIndication?.text ?? null}
          emptyLabel={EMPTY}
        />

        {studySlots.map((slot: StudyComparisonSlot, displayIndex: number) => {
          const { slotKey, prevStudy, currStudy, matchType } = slot;
          const canCopyStudy = currStudy == null && prevStudy != null && onCopyStudy != null;
          const studyCopying = prevStudy != null && copyingStudyId === prevStudy.id;
          const rowStudyId = currStudy?.id ?? slotKey;
          const matchLabel = matchType === 'sample-size' ? 'Matched by sample size' : undefined;

          return (
            <StudyComparisonBlock
              key={`study-${slotKey}`}
              slotKey={slotKey}
              displayIndex={displayIndex}
              matchLabel={matchLabel}
              prevStudy={prevStudy}
              currStudy={currStudy}
              rowStudyId={rowStudyId}
              canCopyStudy={canCopyStudy}
              studyCopying={studyCopying}
              isCopying={isCopying}
              onCopyStudyClick={handleCopyStudyClick}
              onEditStudyClick={onEditStudy != null ? handleEditStudyClick : undefined}
              onSaveStudy={onSaveStudy}
              editingStudyId={editingStudyId ?? null}
              studyKeysOrder={STUDY_KEYS_ORDER}
              attributes={getAttributesForStudy(prevStudy, currStudy)}
              formatStudyValue={formatStudyValue}
              getAttributeValue={getAttributeValue}
              renderCurrentCellForStudyKey={renderCurrentCellForStudyKey}
              renderCurrentCellForAttribute={renderCurrentCellForAttribute}
            />
          );
        })}
      </Box>

      <IndicationComparisonActions
        showActions={showActions}
        hasAnyAction={hasAnyAction}
        currIndication={currIndication}
        isCopying={isCopying}
        onCopyIndication={onCopyIndication}
        onCopy={onCopy}
        onAcceptCurrent={onAcceptCurrent}
      />
    </Box>
  );
};

export default React.memo(IndicationComparison);
